// Membership wizard review & activate (SLICE-MEMBERSHIP-PASS).
//
// The membership analogue of StepReview. Simpler — no stamps/milestones; the
// only activation gap a membership can have is a malformed expiry shape
// (collectMembershipActivationGaps == expirationShapeIssues). Mirrors the
// server's MembershipActivateSchema so the owner isn't surprised by a 422.

import {
  collectMembershipActivationGaps,
  type MembershipConfig,
} from '@naya/shared'

import type { BuilderController } from '../hooks/useBuilder.js'
import type { WizardStepKey } from '../builderState.js'

function SummaryBlock({
  title,
  step,
  onEdit,
  children,
}: {
  title: string
  step: WizardStepKey
  onEdit: (step: WizardStepKey) => void
  children: React.ReactNode
}) {
  return (
    <div className="program-summary-block">
      <div className="program-summary-head">
        <h3>{title}</h3>
        <button
          type="button"
          className="program-link-btn"
          onClick={() => onEdit(step)}
        >
          Edit
        </button>
      </div>
      <div className="program-summary-body">{children}</div>
    </div>
  )
}

// Human-readable expiry line for the summary.
function expirySummary(draft: BuilderController['draft']): string {
  if (draft.expiration_mode === 'none') return 'Never expires'
  if (draft.expiration_mode === 'fixed') {
    return draft.expiration_fixed_date
      ? `Expires on ${draft.expiration_fixed_date}`
      : 'Expires on a fixed date'
  }
  const amount = draft.expiration_relative_amount
  const unit = draft.expiration_relative_unit
  return amount && unit ? `Expires ${amount} ${unit} after joining` : 'Expires after a period'
}

export function StepMembershipReview({
  builder,
  onEditStep,
  onRequestActivate,
}: {
  builder: BuilderController
  onEditStep: (step: WizardStepKey) => void
  /** Opens the confirmation modal — the actual API call lives in the wizard. */
  onRequestActivate: () => void
}) {
  const { draft } = builder
  const config = draft.type_config as MembershipConfig | null

  // Mirror the server's membership activation check (expiry shape only).
  const gaps = collectMembershipActivationGaps({
    name: draft.name,
    onboarding_fields: draft.onboarding_fields,
    pass_design: draft.pass_design,
    back_links: draft.back_links,
    owner_terms: draft.owner_terms,
    expiration_mode: draft.expiration_mode,
    expiration_relative_unit: draft.expiration_relative_unit,
    expiration_relative_amount: draft.expiration_relative_amount,
    expiration_fixed_date: draft.expiration_fixed_date,
    type_config: config ?? {
      tiers_enabled: false,
      tiers: [],
      benefits: [],
      member_number_label: 'Member No.',
      self_enroll_enabled: false,
    },
  })
  const canActivate = gaps.length === 0

  const enabledOptional = (
    ['last_name', 'birthday', 'phone'] as const
  ).filter((f) => draft.onboarding_fields[f].enabled)

  return (
    <div className="program-step">
      <h2>Review and activate</h2>
      <p className="program-step-sub">
        Check everything over. Once you activate, members can be issued a pass.
      </p>

      <SummaryBlock title="Membership" step="membership" onEdit={onEditStep}>
        <p>
          {config?.tiers_enabled && config.tiers.length > 0
            ? `${config.tiers.filter((t) => t.trim()).length} tier${
                config.tiers.filter((t) => t.trim()).length === 1 ? '' : 's'
              } · `
            : ''}
          {config?.benefits.length ?? 0} benefit
          {(config?.benefits.length ?? 0) === 1 ? '' : 's'} ·{' '}
          {config?.self_enroll_enabled
            ? 'Self-enroll on'
            : 'Issued in store only'}
        </p>
        {config?.tiers_enabled && config.tiers.some((t) => t.trim()) && (
          <p className="program-summary-sub">
            Tiers: {config.tiers.filter((t) => t.trim()).join(', ')}
          </p>
        )}
        {config && config.benefits.length > 0 && (
          <ul className="program-summary-list">
            {config.benefits.map((b, i) => (
              <li key={i}>{b || <em>(empty benefit)</em>}</li>
            ))}
          </ul>
        )}
      </SummaryBlock>

      <SummaryBlock title="Sign-up info" step="onboarding" onEdit={onEditStep}>
        <p>
          First name, Email
          {enabledOptional.length > 0 && (
            <>
              {', '}
              {enabledOptional.map((f) => f.replace('_', ' ')).join(', ')}
            </>
          )}
        </p>
      </SummaryBlock>

      <SummaryBlock title="Pass front" step="front" onEdit={onEditStep}>
        <p className="program-summary-swatches">
          <span
            className="program-summary-swatch"
            style={{ background: draft.pass_design.primary_color }}
          />
          {draft.pass_design.primary_color}
          <span
            className="program-summary-swatch"
            style={{ background: draft.pass_design.text_color }}
          />
          {draft.pass_design.text_color}
        </p>
      </SummaryBlock>

      <SummaryBlock title="Pass back" step="back" onEdit={onEditStep}>
        <p>
          {draft.back_links.length} link
          {draft.back_links.length === 1 ? '' : 's'}
          {draft.owner_terms.trim() ? ' · custom terms added' : ''}
        </p>
      </SummaryBlock>

      <SummaryBlock title="Expiry" step="expiry" onEdit={onEditStep}>
        <p>{expirySummary(draft)}</p>
      </SummaryBlock>

      {!canActivate && (
        <div className="program-gap-list" role="alert">
          <strong>Before you can activate:</strong>
          <ul>
            {gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="program-activate-warning">
        After activating, you can't change the sign-up fields or the expiry. You
        can still edit colors, links, terms, the tier, and benefits later.
        Activate when you're ready to start issuing memberships.
      </div>

      <button
        type="button"
        className="owner-btn owner-btn-primary program-activate-btn"
        disabled={!canActivate}
        onClick={onRequestActivate}
      >
        Activate membership
      </button>
    </div>
  )
}
